import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import type { UserDocumentParticipantRole } from "@/api/models"

import { useLogin } from "@/api/auth-api"
import { queryKeys } from "@/lib/query-keys"
import { DocumentService } from "@/services/document-service"
import { SettingsService } from "@/services/settings-service"

function getSettingsToken(): string | undefined {
  try {
    return SettingsService.getInstance().token
  } catch {
    return undefined
  }
}

export function useDocuments(projectId?: string, enabled: boolean = true) {
  const idToken = getSettingsToken()
  const isAuthReady = !useLogin || !!idToken

  return useQuery({
    queryKey: queryKeys.documents(projectId),
    queryFn: () => DocumentService.listDocuments(projectId, idToken),
    enabled: isAuthReady && enabled,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    refetchOnWindowFocus: true,
  })
}

export function useDocument(documentId: string | undefined) {
  const idToken = getSettingsToken()
  const isAuthReady = !useLogin || !!idToken

  return useQuery({
    queryKey: queryKeys.document(documentId || ""),
    queryFn: () => DocumentService.getDocument(documentId!, idToken),
    enabled: !!documentId && isAuthReady,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  })
}

export function useDocumentTitles(documentIds: string[]): Record<string, string> {
  const idToken = getSettingsToken()
  const isAuthReady = !useLogin || !!idToken
  const uniqueIds = [...new Set(documentIds)].filter(Boolean)

  const results = useQueries({
    queries: uniqueIds.map((documentId) => ({
      queryKey: queryKeys.document(documentId),
      queryFn: () => DocumentService.getDocument(documentId, idToken),
      enabled: isAuthReady && !!documentId,
      staleTime: 1000 * 30,
      gcTime: 1000 * 60 * 5,
    })),
  })

  const titleMap: Record<string, string> = {}
  results.forEach((result, i) => {
    const id = uniqueIds[i]
    if (id && result.data?.title) {
      titleMap[id] = result.data.title
    }
  })
  return titleMap
}

export function useDocumentVersions(documentId: string | undefined) {
  const idToken = getSettingsToken()
  const isAuthReady = !useLogin || !!idToken

  return useQuery({
    queryKey: queryKeys.documentVersions(documentId || ""),
    queryFn: () => DocumentService.listVersions(documentId!, idToken),
    enabled: !!documentId && isAuthReady,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  })
}

export function useCreateDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: Parameters<typeof DocumentService.createDocument>[0]) =>
      DocumentService.createDocument(payload, getSettingsToken()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
    },
    onError: (error: Error) => {
      toast.error("Failed to create document", {
        description: error.message || "An error occurred",
      })
    },
  })
}

export function useCreateDocumentFromMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ messageId, title }: { messageId: string; title?: string }) =>
      DocumentService.createDocumentFromMessage(messageId, title, getSettingsToken()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
    },
    onError: (error: Error) => {
      toast.error("Failed to create document", {
        description: error.message || "An error occurred",
      })
    },
  })
}

export function useUpdateDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      documentId,
      payload,
    }: {
      documentId: string
      payload: Parameters<typeof DocumentService.updateDocument>[1]
    }) => DocumentService.updateDocument(documentId, payload, getSettingsToken()),
    onSuccess: (updatedDocument) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
      void queryClient.invalidateQueries({ queryKey: queryKeys.document(updatedDocument.id) })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documentVersions(updatedDocument.id),
      })
    },
    onError: (error: Error) => {
      toast.error("Failed to update document", {
        description: error.message || "An error occurred",
      })
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) =>
      DocumentService.deleteDocument(documentId, getSettingsToken()),
    onSuccess: (_, documentId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
      queryClient.removeQueries({ queryKey: queryKeys.document(documentId) })
      queryClient.removeQueries({ queryKey: queryKeys.documentVersions(documentId) })
      queryClient.removeQueries({ queryKey: queryKeys.documentParticipants(documentId) })
    },
    onError: (error: Error) => {
      toast.error("Failed to delete document", {
        description: error.message || "An error occurred",
      })
    },
  })
}

export function useDocumentParticipants(documentId: string | undefined) {
  const idToken = getSettingsToken()
  const isAuthReady = !useLogin || !!idToken

  return useQuery({
    queryKey: queryKeys.documentParticipants(documentId || ""),
    queryFn: () => DocumentService.listParticipants(documentId!, idToken),
    enabled: !!documentId && isAuthReady,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  })
}

export function useAddDocumentParticipant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      documentId,
      participant,
    }: {
      documentId: string
      participant: {
        oid: string
        displayName: string
        type: "user" | "group"
        role: UserDocumentParticipantRole
      }
    }) => DocumentService.addParticipant(documentId, participant, getSettingsToken()),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documentParticipants(variables.documentId),
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
    },
  })
}

export function useUpdateDocumentParticipantRole() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      documentId,
      participantOid,
      role,
    }: {
      documentId: string
      participantOid: string
      role: UserDocumentParticipantRole
    }) =>
      DocumentService.updateParticipantRole(
        documentId,
        { oid: participantOid, role },
        getSettingsToken()
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documentParticipants(variables.documentId),
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
    },
  })
}

export function useRemoveDocumentParticipant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ documentId, participantOid }: { documentId: string; participantOid: string }) =>
      DocumentService.removeParticipant(documentId, participantOid, getSettingsToken()),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documentParticipants(variables.documentId),
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
    },
  })
}

export function useTransferDocumentOwnership() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ documentId, newOwnerOid }: { documentId: string; newOwnerOid: string }) =>
      DocumentService.transferOwnership(documentId, newOwnerOid, getSettingsToken()),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.documentParticipants(variables.documentId),
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.document(variables.documentId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.documents() })
      toast.success("Ownership transferred successfully", {
        description: "You are now a reader of this document.",
      })
    },
    onError: (error: Error) => {
      toast.error("Failed to transfer ownership", {
        description: error.message || "An error occurred",
      })
    },
  })
}
